import React, { FC } from "react";
import { Form, Formik } from "formik";
import { ButtonPrimary, ButtonSecondary, CheckboxInput, Textarea } from "@shared/components";
import { Modal } from "@shared/components/Modal";
import * as Yup from "yup";

import { CancellationReasons } from "./types";

import "./index.scss";

export type ModalCancelPayPerLeadSubsProps = {
  onClose: () => void;
  onSuccess: (values: CancellationReasons) => void;
};

const cancellationReasons: Record<string, CancellationReasons["cancellation_reason"]> = {
  cost_too_high: "Cost too high",
  not_enough_leads: "Not getting enough leads",
  not_enough_features: "Not enough features",
  switching_strategies: "Switching strategies",
  other: "Other",
};

const formValidationSchema = Yup.object().shape({
  cancellation_reason: Yup.string().required("Please select the reason to confirm cancellation"),
  cancellation_reason_other: Yup.string().when("cancellation_reason", {
    is: "other",
    then: Yup.string()
      .trim()
      .max(250, `Please keep your explanation under 250 characters`)
      .required(`When choosing 'Other', please explain why (up to 250 characters)`),
    otherwise: Yup.string().optional(),
  }),
});

export const ModalCancelPayPerLeadSubs: FC<ModalCancelPayPerLeadSubsProps> = (props) => {
  const { onClose, onSuccess } = props;

  const initialValues: CancellationReasons = { cancellation_reason: "", cancellation_reason_other: "" };

  const handleSubmit = async ({ cancellation_reason, cancellation_reason_other }: Record<string, string>) => {
    onSuccess({
      cancellation_reason: cancellationReasons[cancellation_reason],
      ...(cancellation_reason_other && { cancellation_reason_other }),
    });
  };

  return (
    <Modal>
      <Formik
        validationSchema={formValidationSchema}
        initialValues={initialValues}
        enableReinitialize={true}
        validateOnBlur={false}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, isSubmitting, setValues, setTouched, handleSubmit }) => {
          const showErrorMessage =
            touched.cancellation_reason && touched.cancellation_reason_other && Object.keys(errors).length;

          const errorMessage = showErrorMessage ? (
            <Modal.Error>{errors.cancellation_reason ?? errors.cancellation_reason_other}</Modal.Error>
          ) : null;

          return (
            <Form onSubmit={handleSubmit}>
              <Modal.Section>
                <Modal.Header>
                  <Modal.Title>Cancel Subscription</Modal.Title>
                  <Modal.Text>
                    Please confirm you want to cancel your subscription. We would appreciate it if you could share your
                    reason for the cancellation.
                  </Modal.Text>
                </Modal.Header>

                <Modal.Content>
                  <div className="modal-content-inner">
                    <div className="modal-content-title">Cancellation reason</div>

                    <CheckboxInput
                      name="cost_too_high"
                      label="Cost too high"
                      value={values.cancellation_reason === "cost_too_high" ? 1 : 0}
                      onChange={() => {
                        setValues({ cancellation_reason: "cost_too_high", cancellation_reason_other: "" }, true);
                        setTouched({ cancellation_reason: true });
                      }}
                    />

                    <CheckboxInput
                      name="not_enough_leads"
                      label="Not getting enough leads"
                      value={values.cancellation_reason === "not_enough_leads" ? 1 : 0}
                      onChange={() => {
                        setValues({ cancellation_reason: "not_enough_leads", cancellation_reason_other: "" }, true);
                        setTouched({ cancellation_reason: true });
                      }}
                    />

                    <CheckboxInput
                      name="not_enough_features"
                      label="Not enough features"
                      value={values.cancellation_reason === "not_enough_features" ? 1 : 0}
                      onChange={() => {
                        setValues({ cancellation_reason: "not_enough_features", cancellation_reason_other: "" }, true);
                        setTouched({ cancellation_reason: true });
                      }}
                    />

                    <CheckboxInput
                      name="switching_strategies"
                      label="Switching strategies"
                      value={values.cancellation_reason === "switching_strategies" ? 1 : 0}
                      onChange={() => {
                        setValues({ cancellation_reason: "switching_strategies", cancellation_reason_other: "" }, true);
                        setTouched({ cancellation_reason: true });
                      }}
                    />

                    <CheckboxInput
                      name="other"
                      label="Other"
                      value={values.cancellation_reason === "other" ? 1 : 0}
                      onChange={() => {
                        setValues({ ...values, cancellation_reason: "other" });
                        setTouched({ cancellation_reason: true });
                      }}
                    />

                    {values.cancellation_reason === "other" && (
                      <Textarea
                        name="cancellation_reason_other"
                        value={values.cancellation_reason_other}
                        placeholder="Enter your reason"
                        characterCounterLimit={250}
                        rows={5}
                        onChange={(evt: React.ChangeEvent<HTMLTextAreaElement>) => {
                          setValues({ ...values, cancellation_reason_other: evt.target.value });
                          setTouched({ cancellation_reason: true, cancellation_reason_other: true });
                        }}
                      />
                    )}
                  </div>
                </Modal.Content>

                {errorMessage}

                <Modal.Actions>
                  <ButtonSecondary onClick={onClose}>Back</ButtonSecondary>
                  <ButtonPrimary type="submit" disabled={isSubmitting}>
                    Confirm Cancellation
                  </ButtonPrimary>
                </Modal.Actions>
              </Modal.Section>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};
